import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { useJobCohorts, jobsError } from '../../hooks/useJobDigest';
import { count } from './ui';

// In the menu, show each cohort's graduates; once chosen, just its name.
function formatOption(option, { context }) {
  if (context !== 'menu') return option.label;
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{option.label}</span>
      <span className="text-[11px] text-gray-500">
        {count(option.cohort.members)} members ·{' '}
        {count(option.cohort.graduates)} certified
        {option.cohort.students_emailed
          ? ` · ${count(option.cohort.students_emailed)} emailed`
          : ''}
      </span>
    </div>
  );
}

// Picks one or more cohorts. Typing filters the list; "Add all matching"
// selects every cohort whose name matches, e.g. all the HPF cohorts.
export default function CohortPicker({ value, onChange }) {
  const { data: cohorts = [], isLoading, error } = useJobCohorts();
  const [input, setInput] = useState('');

  const options = useMemo(
    () =>
      cohorts.map((c) => ({
        value: c.id,
        label: c.name,
        cohort: c,
      })),
    [cohorts]
  );
  const selected = options.filter((o) => value.includes(o.value));
  const term = input.trim().toLowerCase();
  const matching = term
    ? options.filter(
        (o) => o.label.toLowerCase().includes(term) && !value.includes(o.value)
      )
    : [];

  return (
    <div className="space-y-1">
      <Select
        isMulti
        isLoading={isLoading}
        options={options}
        value={selected}
        inputValue={input}
        onInputChange={(text, meta) => {
          if (meta.action === 'input-change') setInput(text);
          if (meta.action === 'menu-close') setInput('');
        }}
        onChange={(chosen) => onChange((chosen ?? []).map((o) => o.value))}
        placeholder="Choose cohorts, e.g. type HPF"
        noOptionsMessage={() =>
          error ? jobsError(error, "Couldn't load cohorts.") : 'No cohorts'
        }
        formatOptionLabel={formatOption}
        classNamePrefix="cohort-picker"
      />
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {matching.length > 1 && (
          <button
            type="button"
            className="text-claret-500 hover:underline"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange([...value, ...matching.map((o) => o.value)]);
              setInput('');
            }}
          >
            Add all {matching.length} matching &ldquo;{input.trim()}&rdquo;
          </button>
        )}
        {value.length > 0 && (
          <>
            <span className="text-gray-500">
              {value.length} cohort{value.length === 1 ? '' : 's'} ·{' '}
              {count(
                selected.reduce((sum, o) => sum + (o.cohort.members || 0), 0)
              )}{' '}
              members ·{' '}
              {count(
                selected.reduce((sum, o) => sum + (o.cohort.graduates || 0), 0)
              )}{' '}
              certified here
            </span>
            <button
              type="button"
              className="text-gray-500 hover:underline"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </>
        )}
      </div>
    </div>
  );
}
